<script setup lang="ts">
const route = useRoute()
const slug = route.params.slug as string

const { data: project } = await useFetch(`/api/projects/${slug}`)

if (!project.value) {
  throw createError({ statusCode: 404, statusMessage: 'Project not found' })
}

useSeoMeta({
  title: `${(project.value as any).title} — Shakir Abdulrahim`,
  ogTitle: `${(project.value as any).title} — Shakir Abdulrahim`,
  description: (project.value as any).description,
})
</script>

<template>
  <div class="py-24">
    <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <!-- Back -->
      <NuxtLink to="/projects" class="mb-8 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-amber)]">
        <UIcon name="i-lucide-arrow-left" class="size-4" />
        Back to projects
      </NuxtLink>

      <!-- Header -->
      <div class="mb-3 flex flex-wrap items-center gap-2">
        <span class="rounded-full bg-[var(--color-amber)]/10 px-3 py-1 text-xs font-semibold text-[var(--color-amber)]">
          {{ (project as any).category }}
        </span>
        <span v-if="(project as any).featured" class="rounded-full bg-[var(--color-steel-blue)]/10 px-3 py-1 text-xs font-semibold text-[var(--color-steel-blue)]">
          Featured
        </span>
      </div>
      <h1 class="mb-4 text-4xl font-bold text-[var(--color-text)] sm:text-5xl">
        {{ (project as any).title }}
      </h1>
      <p class="mb-6 text-lg text-[var(--color-text-muted)]">
        {{ (project as any).description }}
      </p>

      <!-- Tags -->
      <div class="mb-8 flex flex-wrap gap-2">
        <span
          v-for="tag in (project as any).tags"
          :key="tag"
          class="rounded-md border border-[var(--color-card-border)] bg-[var(--color-surface-hover)] px-3 py-1 text-sm text-[var(--color-text-muted)]"
        >
          {{ tag }}
        </span>
      </div>

      <!-- Links -->
      <div v-if="(project as any).url || (project as any).github_url" class="mb-10 flex flex-wrap gap-3">
        <UButton
          v-if="(project as any).url"
          :to="(project as any).url"
          target="_blank"
          size="lg"
          class="bg-[var(--color-amber)] text-[var(--color-dark-base)] hover:bg-[var(--color-amber)]/90"
          leading-icon="i-lucide-external-link"
        >
          Visit Site
        </UButton>
        <UButton
          v-if="(project as any).github_url"
          :to="(project as any).github_url"
          target="_blank"
          size="lg"
          variant="outline"
          class="border-[var(--color-card-border)] text-[var(--color-text)] hover:border-[var(--color-amber)]"
          leading-icon="i-simple-icons-github"
        >
          View Source
        </UButton>
      </div>

      <!-- Content -->
      <div
        v-if="(project as any).content"
        class="prose dark:prose-invert max-w-none text-(--color-text-muted) prose-headings:text-(--color-text) prose-a:text-amber prose-strong:text-(--color-text) prose-code:text-amber"
        v-html="(project as any).content"
      />
    </div>
  </div>
</template>
